import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Typography } from '@mui/material';
import { Location } from "history";
import React, { useEffect, useState } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";

import { StyledDialog } from './SaveExitModal.styles';

type Props = {
  content: string;
  title: string;
  actionTitle: string,
  confirmNavigationTitle: string
  onActionClick: () => void,
  onNavigateAway?: () => void,
  isBlocked: boolean;
};

export const SaveExitModal: React.FC<Props> = (props: Props) => {
  const { content, title, isBlocked, actionTitle, confirmNavigationTitle, onActionClick, onNavigateAway: onConfirmNavigationClick } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const [lastLocation, setLastLocation] = useState(location);
  const [shouldUnload, setShouldUnload] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
    setShouldUnload(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const showModal = (nextLocation: React.SetStateAction<Location<unknown>>) => {
    openModal();
    setLastLocation(nextLocation);
  };

  const handleBlockedRoute = (nextLocation: React.SetStateAction<Location<unknown>>) => {
    if (!confirmedNavigation && isBlocked) {
      showModal(nextLocation);
      return false;
    } else if(onConfirmNavigationClick) {
      onConfirmNavigationClick();
    }

    return true;
  };

  const handleActionClick = () => {
    onActionClick();
    closeModal();
  };

  const handleConfirmNavigationClick = () => {
    closeModal();
    setConfirmedNavigation(true);
    if(onConfirmNavigationClick) {
      onConfirmNavigationClick();
    }
  };

  // Block react routes
  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Navigate to the previous blocked location with specified function
      setShouldUnload(true);
      history.push(lastLocation.pathname);
    }
  }, [confirmedNavigation, lastLocation, history]);

  // Block non-react routes
  useEffect(() => {
    const unload = (event: { returnValue: string; }) => {
      if (isBlocked && !shouldUnload) {
        event.returnValue = content;
      }
      if (shouldUnload) {
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", unload);

    return () => window.removeEventListener("beforeunload", unload);
  }, [isBlocked, content, shouldUnload]);

  return (
    <>
      <Prompt when message={handleBlockedRoute} />

      <StyledDialog
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby=""
        aria-describedby="alert_dialog_description_save"
      >
        <DialogContent>
          <DialogContentText id="alert_dialog_description_save">
            Your changes have not been saved. <br />Save your changes now or exit without saving.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id="btn_save" onClick={handleActionClick} autoFocus variant="contained">{actionTitle}</Button>
          <Button id="btn_exit" onClick={handleConfirmNavigationClick} variant="outlined">{confirmNavigationTitle}</Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};